import { useState, useEffect, useRef, useCallback } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  color: string;
}

export default function NeuralHero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const initParticles = useCallback((w: number, h: number): Particle[] => {
    const colors = ['#00D4FF', '#00FFA3', '#A855F7', '#FF6B35', '#10B981'];
    return Array.from({ length: 60 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.8,
      vy: (Math.random() - 0.5) * 0.8,
      radius: Math.random() * 2 + 1,
      opacity: Math.random() * 0.5 + 0.3,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
  }, []);

  useEffect(() => {
    const update = () => {
      if (canvasRef.current) {
        setDimensions({
          width: canvasRef.current.offsetWidth,
          height: canvasRef.current.offsetHeight,
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const particles = initParticles(dimensions.width, dimensions.height);
    let animationFrame: number;

    const draw = () => {
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);

      // Update positions
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > dimensions.width) p.vx *= -1;
        if (p.y < 0 || p.y > dimensions.height) p.vy *= -1;
      });

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 212, 255, ${0.15 * (1 - dist / 150)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      // Draw particles
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
      });

      ctx.globalAlpha = 1;
      animationFrame = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animationFrame);
  }, [dimensions, initParticles]);

  return (
    <section className="relative min-h-[420px] md:min-h-[600px] flex flex-col items-center justify-center text-center px-6 pb-10 md:pb-14 overflow-hidden">
      {/* Canvas background */}
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        className="absolute inset-0 w-full h-full"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030712]/50 to-[#030712]" />

      {/* Content */}
      <div className="relative z-10 pt-20 md:pt-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D4FF]/10 border border-[#00D4FF]/20 rounded-full mb-8 animate-pulse">
          <span className="w-2 h-2 bg-[#00FFA3] rounded-full"></span>
          <span className="text-sm text-[#00D4FF]">23款AI产品 · 全域覆盖</span>
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
          <span className="text-white">确定性农业</span>
          <br />
          <span className="bg-gradient-to-r from-[#00D4FF] via-[#00FFA3] to-[#00D4FF] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
            人工智能产品矩阵
          </span>
        </h1>

        <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto mb-8">
          以AI重构农业生产确定性，从战略决策到一线执行，全链路智能化赋能
        </p>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 relative z-10 w-full max-w-5xl mx-auto">
          {[
            { n: '23', t: 'AI产品' },
            { n: '4', t: '产品层级' },
            { n: '56', t: '协同连线' },
            { n: '100%', t: '智能化' },
          ].map((s) => (
            <div
              key={s.t}
              className="rounded-3xl bg-white/5 border border-[#00D4FF]/10 px-6 md:px-8 py-6 text-center"
            >
              <div className="text-3xl md:text-4xl font-bold text-[#00D4FF]">{s.n}</div>
              <div className="text-white/60 mt-2 text-sm md:text-base">{s.t}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}